// AIAssistant.jsx - World-class AI Support Assistant for Pro IRP
// Floating chat interface with streaming responses, context awareness, and premium UX

import React, { useState, useRef, useEffect } from 'react';
import { useAI } from '../context/AIContext';
import {
  MessageCircle, X, Minimize2, Maximize2, Send, Bot, User,
  RotateCcw, ChevronRight, Sparkles, AlertCircle, Loader2,
  ThumbsUp, ThumbsDown, ExternalLink, History, Clock,
  HelpCircle, Zap, ArrowRight
} from 'lucide-react';

export default function AIAssistant() {
  const {
    isOpen, isMinimized, messages, loading, error, isStreaming,
    suggestions, pageContext, usage, history, showHistory, welcomeMessage,
    sendMessage, startNewConversation, toggleOpen, minimize, expand,
    executeAction, submitFeedback, toggleHistory, loadConversation
  } = useAI();

  const [input, setInput] = useState('');
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [showHint, setShowHint] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(() => {
    return localStorage.getItem('ai-hint-dismissed') === 'true';
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  // Show hint after a short delay for first-time users
  useEffect(() => {
    if (!hintDismissed && !isOpen) {
      const timer = setTimeout(() => setShowHint(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [hintDismissed, isOpen]);

  const dismissHint = () => {
    setShowHint(false);
    setHintDismissed(true);
    localStorage.setItem('ai-hint-dismissed', 'true');
  };

  // Focus input when opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  const handleSend = (e) => {
    e.preventDefault();
    if (input.trim() && !loading && !isStreaming) {
      sendMessage(input);
      setInput('');
    }
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };

  const handleFeedback = async (messageId, isPositive) => {
    if (feedbackGiven[messageId]) return;
    setFeedbackGiven(prev => ({ ...prev, [messageId]: true }));
    await submitFeedback(isPositive ? 5 : 1, null);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Floating button when closed
  if (!isOpen) {
    return (
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
        {/* First-time hint bubble */}
        {showHint && (
          <div className="absolute bottom-full right-0 mb-3 animate-fade-in">
            <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-3 max-w-[240px] sm:max-w-[280px]">
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">Hi! I'm your AI Assistant</p>
                  <p className="text-xs text-gray-500 mt-0.5">Click here anytime to ask questions about Pro IRP</p>
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); dismissHint(); }}
                className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Got it!
              </button>
            </div>
            {/* Arrow pointing to button */}
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-gray-200 transform rotate-45"></div>
          </div>
        )}

        <button
          onClick={() => { toggleOpen(); dismissHint(); }}
          className="bg-gradient-to-r from-[#172A3A] to-slate-700 text-white rounded-full p-3 sm:p-4 shadow-2xl hover:shadow-blue-500/20 hover:scale-105 transition-all duration-200 group"
          title="Open AI Assistant"
        >
          <div className="relative">
            <Bot className="w-5 h-5 sm:w-6 sm:h-6" />
            <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3 absolute -top-1 -right-1 text-yellow-300 animate-pulse" />
          </div>
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs sm:text-sm px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap hidden sm:block">
            Need help? Ask me anything!
          </span>
        </button>
      </div>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <div
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 bg-white rounded-full shadow-2xl border border-gray-200 flex items-center gap-2 px-3 sm:px-4 py-2 cursor-pointer hover:shadow-lg transition-shadow"
        onClick={expand}
      >
        <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-[#172A3A]" />
        <span className="text-xs sm:text-sm font-medium text-gray-700">Pro IRP Assistant</span>
        {messages.length > 0 && (
          <span className="bg-blue-100 text-blue-700 text-xs px-1.5 sm:px-2 py-0.5 rounded-full">
            {messages.length}
          </span>
        )}
        <Maximize2 className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
      </div>
    );
  }

  // Full chat interface
  return (
    <div className="fixed inset-0 sm:inset-auto sm:bottom-6 sm:right-6 z-50 sm:w-[420px] sm:max-h-[600px] bg-white sm:rounded-2xl shadow-2xl border-0 sm:border border-gray-200 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#172A3A] to-slate-700 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
            <Bot className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm sm:text-base">Pro IRP Assistant</h3>
            <p className="text-xs text-gray-300">Powered by AI</p>
          </div>
        </div>
        <div className="flex items-center gap-0.5 sm:gap-1">
          <button
            onClick={toggleHistory}
            className={`p-1.5 sm:p-2 rounded-lg transition-colors ${showHistory ? 'bg-white/20' : 'hover:bg-white/10'}`}
            title="Conversation history"
          >
            <History className="w-4 h-4" />
          </button>
          <button
            onClick={startNewConversation}
            className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="New conversation"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            onClick={minimize}
            className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors hidden sm:block"
            title="Minimize"
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          <button
            onClick={toggleOpen}
            className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Context & Usage Bar */}
      <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-50 border-b text-xs text-gray-500 flex justify-between items-center flex-shrink-0">
        <span className="flex items-center gap-1">
          <HelpCircle className="w-3 h-3" />
          <span className="hidden sm:inline">Page:</span> {pageContext}
        </span>
        <span className="flex items-center gap-1">
          <Zap className="w-3 h-3" />
          {usage.messagesUsed}/{usage.messagesLimit} <span className="hidden sm:inline">today</span>
        </span>
      </div>

      {/* History Panel */}
      {showHistory && (
        <div className="absolute inset-0 top-[96px] sm:top-[100px] bg-white z-10 flex flex-col">
          <div className="p-3 sm:p-4 border-b bg-gray-50">
            <h4 className="font-semibold text-gray-700 text-sm sm:text-base">Recent Conversations</h4>
          </div>
          <div className="flex-1 overflow-y-auto p-2 sm:p-3 space-y-2">
            {history.length === 0 ? (
              <p className="text-center text-gray-400 py-8 text-sm">No previous conversations</p>
            ) : (
              history.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => loadConversation(conv.id)}
                  className="w-full text-left p-2.5 sm:p-3 rounded-lg hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200"
                >
                  <p className="text-xs sm:text-sm font-medium text-gray-800 truncate">
                    {conv.first_message || 'Conversation'}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                    <Clock className="w-3 h-3" />
                    {new Date(conv.created_at).toLocaleDateString()}
                    <span className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">{conv.context_page}</span>
                  </div>
                </button>
              ))
            )}
          </div>
          <div className="p-3 border-t">
            <button
              onClick={toggleHistory}
              className="w-full py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Close History
            </button>
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 min-h-0">
        {messages.length === 0 ? (
          <div className="text-center py-4 sm:py-6">
            <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
              <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-[#172A3A]" />
            </div>
            <h4 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">How can I help?</h4>
            <p className="text-xs sm:text-sm text-gray-500 mb-4 px-2">
              {welcomeMessage}
            </p>

            {/* Quick suggestions */}
            <div className="space-y-2 px-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Try asking:</p>
              {suggestions.slice(0, 3).map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="block w-full text-left px-3 py-2.5 text-xs sm:text-sm bg-gray-50 hover:bg-blue-50 rounded-xl text-gray-700 hover:text-blue-700 transition-colors border border-transparent hover:border-blue-200"
                >
                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 text-gray-400" />
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[88%] ${
                  msg.role === 'user'
                    ? 'bg-[#172A3A] text-white rounded-2xl rounded-br-md'
                    : msg.role === 'system'
                    ? 'bg-amber-50 text-amber-800 border border-amber-200 rounded-2xl'
                    : 'bg-gray-100 text-gray-800 rounded-2xl rounded-bl-md'
                } px-3 sm:px-4 py-2.5 sm:py-3`}>
                  {/* Assistant header */}
                  {msg.role === 'assistant' && (
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 text-xs text-gray-500">
                      <Bot className="w-3 h-3" />
                      <span>Pro IRP Assistant</span>
                      {msg.isStreaming && (
                        <span className="flex items-center gap-1 text-blue-500">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          typing...
                        </span>
                      )}
                    </div>
                  )}

                  {/* Message content */}
                  <div className="text-xs sm:text-sm whitespace-pre-wrap leading-relaxed">
                    {msg.content}
                    {msg.isStreaming && !msg.content && (
                      <span className="inline-flex items-center gap-1 text-gray-400">
                        <Loader2 className="w-3 h-3 animate-spin" />
                      </span>
                    )}
                  </div>

                  {/* Suggested actions */}
                  {msg.suggestedActions?.length > 0 && !msg.isStreaming && (
                    <div className="mt-2.5 sm:mt-3 pt-2.5 border-t border-gray-200 space-y-1.5">
                      {msg.suggestedActions.map((action, idx) => (
                        <button
                          key={idx}
                          onClick={() => executeAction(action)}
                          className="flex items-center gap-2 w-full text-left px-2.5 py-2 text-xs bg-white hover:bg-gray-50 rounded-lg border border-gray-200 text-blue-600 hover:text-blue-700 transition-colors"
                        >
                          <ArrowRight className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{action.label}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Timestamp */}
                  <div className={`text-xs mt-1.5 ${msg.role === 'user' ? 'text-gray-300' : 'text-gray-400'}`}>
                    {formatTime(msg.timestamp)}
                  </div>

                  {/* Feedback buttons for assistant messages */}
                  {msg.role === 'assistant' && !msg.isStreaming && (
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200">
                      <span className="text-xs text-gray-400">Helpful?</span>
                      <button
                        onClick={() => handleFeedback(msg.id, true)}
                        className={`p-1 rounded transition-colors ${
                          feedbackGiven[msg.id] ? 'text-green-500' : 'text-gray-400 hover:text-green-500'
                        }`}
                        disabled={feedbackGiven[msg.id]}
                      >
                        <ThumbsUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleFeedback(msg.id, false)}
                        className={`p-1 rounded transition-colors ${
                          feedbackGiven[msg.id] ? 'text-gray-300' : 'text-gray-400 hover:text-red-500'
                        }`}
                        disabled={feedbackGiven[msg.id]}
                      >
                        <ThumbsDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 text-red-600 text-xs sm:text-sm bg-red-50 px-3 py-2.5 rounded-xl">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <form onSubmit={handleSend} className="p-3 sm:p-4 border-t bg-gray-50 flex-shrink-0">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask me anything..."
            className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
            disabled={loading || isStreaming}
          />
          <button
            type="submit"
            disabled={!input.trim() || loading || isStreaming}
            className="px-3 sm:px-4 py-2.5 sm:py-3 bg-[#172A3A] text-white rounded-xl hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            {isStreaming ? (
              <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
            ) : (
              <Send className="w-4 h-4 sm:w-5 sm:h-5" />
            )}
          </button>
        </div>

        {/* Quick suggestion chips when empty */}
        {messages.length > 0 && !loading && !isStreaming && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {suggestions.slice(0, 2).map((suggestion, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className="text-xs px-2.5 py-1 bg-white border border-gray-200 rounded-full text-gray-600 hover:text-blue-600 hover:border-blue-200 transition-colors truncate max-w-[180px]"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </form>
    </div>
  );
}
